import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import {
    FaEnvelope,
    FaArrowLeft,
    FaCheckCircle,
    FaExclamationTriangle,
    FaSpinner
} from "react-icons/fa";

function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        try {
            await api.post("/auth/forgot-password", { email });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to send reset email. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 sm:px-4 py-6 sm:py-8 relative overflow-hidden">

                {/* Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                </div>

                {/* Success Card */}
                <div className="w-full max-w-md bg-white/10 backdrop-blur-lg shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden relative z-10 border border-white/20 p-6 sm:p-8">
                    <div className="text-center">
                        {/* Success Icon */}
                        <div className="mx-auto flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full mb-4 sm:mb-6">
                            <FaCheckCircle className="text-white text-2xl sm:text-3xl" />
                        </div>

                        {/* Success Message */}
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                            Check Your Email!
                        </h1>
                        <p className="text-white/90 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                            We've sent a password reset link to <strong className="text-white">{email}</strong>.
                            Please check your inbox and follow the instructions to reset your password.
                        </p>

                        {/* Additional Info */}
                        <div className="bg-white/10 rounded-lg p-4 mb-6 sm:mb-8">
                            <p className="text-white/80 text-xs sm:text-sm">
                                <strong>Didn't receive the email?</strong><br />
                                Check your spam folder or try again with a different email address.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3 sm:space-y-4">
                            <button
                                onClick={() => {
                                    setSuccess(false);
                                    setEmail("");
                                }}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                            >
                                Send Another Email
                            </button>

                            <Link
                                to="/login"
                                className="w-full inline-flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                            >
                                <FaArrowLeft className="text-sm" />
                                <span>Back to Login</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 sm:px-4 py-6 sm:py-8 relative overflow-hidden">

            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
            </div>

            {/* Page Heading */}
            <div className="w-full text-center mb-6 sm:mb-8 px-4 relative z-10">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-3 sm:mb-4">
                    Forgot Your
                </h1>
                <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl shadow-2xl">
                    <span className="text-2xl sm:text-3xl">🔐</span>
                    <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white">Password?</span>
                </div>
            </div>

            {/* Forgot Password Card */}
            <div className="w-full max-w-md bg-white/10 backdrop-blur-lg shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden relative z-10 border border-white/20">

                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 sm:p-8 text-center">
                    <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🔑</div>
                    <h2 className="text-xl sm:text-2xl font-bold mb-2">Reset Your Password</h2>
                    <p className="text-white/90 text-sm sm:text-base">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>
                </div>

                {/* Form */}
                <div className="p-6 sm:p-8">
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

                        {/* Email Input */}
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-sm font-medium text-white/90">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/95 text-gray-900 placeholder-gray-500 transition-all duration-200"
                                    placeholder="Enter your email address"
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg flex items-center space-x-2">
                                <FaExclamationTriangle className="text-red-400 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-500 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="animate-spin text-sm" />
                                    <span>Sending...</span>
                                </>
                            ) : (
                                <>
                                    <FaEnvelope className="text-sm" />
                                    <span>Send Reset Link</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Back to Login */}
                    <div className="mt-6 sm:mt-8 text-center">
                        <Link
                            to="/login"
                            className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors duration-200"
                        >
                            <FaArrowLeft className="text-sm" />
                            <span className="text-sm sm:text-base">Back to Login</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Help Text */}
            <div className="mt-6 sm:mt-8 text-center max-w-md px-4 relative z-10">
                <p className="text-white/70 text-xs sm:text-sm">
                    Remember your password?{" "}
                    <Link to="/login" className="text-purple-300 hover:text-purple-200 font-medium transition-colors">
                        Sign in here
                    </Link>
                </p>
            </div>
        </div>
    );
}

export default ForgotPassword;
